import React, { useEffect, useState } from 'react'
import { Button, Layout, notification, PageHeader, Space, Switch } from 'antd'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Route } from 'react-router-dom'

import { addNewPage, fetchEventsVenueById, initializePages, newSuiteCardModule, resetPageActionStatus } from '~actions'
import { ResultModal, PageBasicInfo } from '~components'
import { PAGE_TYPES, PAGE_TYPES_PREMIUM_EDITOR } from '~constants'
import { getCurrentUser } from '~context'
import { Spinner } from '~stories'
import { getFileName } from '~utils'
import { validatePage } from '~validators'
import { createEmptyUniversalModule } from '../../components/UniversalTemplateModules/universalTemplateDefaults'
import { getFormUrlValidationMessage } from '../../utils/universalTemplateFormValidation'
import {
  buildUniversalTemplateSlugFromName,
  getUniversalTemplateSlugPrefix,
  getUniversalTemplateSlugSuffix,
  isValidUniversalTemplateSlug,
  normalizeUniversalTemplateSubtype,
  UNIVERSAL_TEMPLATE_SLUG_SUFFIX_RE
} from '../../utils/universalTemplateSlugFromName'
import styles from './NewPageModule.module.css'

const PAGE_TITLE = 'New Page'
const NOTIFICATION_MESSAGES = {
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  DATA_FIELD_ERR: 'Missing required Fields'
}

const CONTENT_MODULE = {
  cta: {
    name: '',
    url: ''
  },
  description: '',
  header: '',
  icon: ''
}

const SUITE_CARD_MODULE = {
  title: '',
  subtitle: '',
  label: '',
  type: '',
  image: {},
  ctas: [],
  related_events: [],
  promotedEvents: [],
  filters: {}
}

const NewPageModule = ({
  addNewPage,
  allSuiteLevels,
  newPageId,
  availableEventsForPromotion,
  newSuiteCardModule,
  resetPageActionStatus,
  suitesAmenities,
  suitesCapacity,
  suitesEventIdeas,
  suitesEventType,
  fetchEventsVenueById,
  upcomingEvents,
  todaysEvent,
  vlpHeroAllEvents: vlpHeroAllEventsProps,
  recommendedEvents,
  venues,
  tourVenues,
  suitesHospitality,
  initializePages,
  suitesSize,
  updateMessage,
  updateStatus
}) => {
  const [selectedType] = PAGE_TYPES
  const [pageModule, setPageModule] = useState({
    cards: [],
    content_modules: [],
    type: selectedType.value,
    hero: {},
    name: '',
    page_meta: {},
    slug: '',
    form: {
      success_screen: {
        title: '',
        message: '',
        button_text: ''
      }
    },
    banner: {},
    recommendations_section_title: '',
    tours_prices: {},
    anchor_links: [],
    marketing_modules: [],
    galleryHeader: '',
    calendar: {},
    upcoming_shows_section_title: '',
    venue_id: '',
    media_row: [],
    category_pills: [],
    marketing_pills: [],
    social_feeds: {},
    content_cards: {
      header: '',
      cards: []
    },
    show_chatbot: false,
    show_optin_form: false,
    chat_pop_up_id: '',
    modules: []
  })
  const [showSpinner, setShowSpinner] = useState(true)
  const [vlpHeroAllEvents, setVLPHeroAllEvents] = useState([])
  const [vlpHeroPromotedEvents, setVLPHeroPromotedEvents] = useState([])
  const [gallery, setGallery] = useState([])
  const { access_token: accessToken, userDetails: { isContentEditor } } = getCurrentUser()
  const [isDataValid, setIsDataValid] = useState(true)

  useEffect(() => {
    if (vlpHeroAllEventsProps) {
      setVLPHeroAllEvents(vlpHeroAllEventsProps)
    }
  }, [vlpHeroAllEventsProps])

  useEffect(() => {
    initializePages(accessToken)
    setShowSpinner(true)
  }, [accessToken, initializePages])

  useEffect(() => {
    if (!venues) return
    setShowSpinner(false)
  }, [venues])

  useEffect(() => {
    if (!upcomingEvents && !recommendedEvents) return
    setShowSpinner(false)
  }, [recommendedEvents, upcomingEvents, venues])

  useEffect(() => {
    newSuiteCardModule(accessToken)
  }, [accessToken, newSuiteCardModule])

  // Mongoose throws "Argument passed in must be a string of 12 bytes…" when an
  // ObjectId reference field is sent as an empty string instead of being omitted.
  const OBJECT_ID_REF_FIELDS = ['venue_id', 'chat_pop_up_id']
  // Fields that only belong to universal-template — strip from all other page types
  // so the existing API contracts for premium-hospitality / venue-landing / tours
  // are not affected by fields introduced for universal-template.
  const UNIVERSAL_TEMPLATE_ONLY_FIELDS = ['modules', 'subtype', 'expires_at']
  const sanitizePagePayload = page => {
    const cleaned = { ...page }
    OBJECT_ID_REF_FIELDS.forEach(field => {
      if (cleaned[field] === '' || cleaned[field] === null || cleaned[field] === undefined) delete cleaned[field]
    })
    if (cleaned.type !== 'universal-template') {
      UNIVERSAL_TEMPLATE_ONLY_FIELDS.forEach(field => delete cleaned[field])
    }
    return cleaned
  }

  const handleSubmit = async () => {
    if (pageModule?.type === 'universal-template') {
      const hasErrors = Object.values(universalTemplateErrors).some(Boolean)
      if (hasErrors) {
        return
      }
    }

    setIsDataValid(false)
    if (PAGE_TYPES_PREMIUM_EDITOR.includes(pageModule.type)) {
      const { isValid, message } = validatePage(pageModule)

      if (!isValid) {
        notification.error({ message })
        setIsDataValid(true)
        return
      }
    }

    if (!validateFields() && pageModule?.type !== 'tours' && pageModule?.type !== 'universal-template') {
      setIsDataValid(true)
      notification.error({ message: NOTIFICATION_MESSAGES.DATA_FIELD_ERR })
      return
    } else {
      setIsDataValid(true)
    }

    if (!isContentEditor) {
      notification.error({ message: NOTIFICATION_MESSAGES.UNAUTHORIZED })
      return
    }

    const payload = sanitizePagePayload({
      ...pageModule,
      vlpHeroPromotedEvents: vlpHeroPromotedEvents.map(event => event?.id)
    })
    console.log('addNewPage payload ==>', payload)
    addNewPage(payload, accessToken)
  }

  const handlePageNameChange = event => {
    const name = event.target.value
    const next = { ...pageModule, name }
    if (pageModule?.type === 'universal-template' && venues?.length) {
      const slug = buildUniversalTemplateSlugFromName(name, pageModule.venue_id, venues, pageModule?.subtype)
      if (slug !== undefined) {
        next.slug = slug
      }
    }
    setPageModule(next)
  }

  const validateFields = () => {
    if (!pageModule.social_feeds.header && !pageModule.social_feeds.embed_url) {
      return false
    }
    if (!pageModule.upcoming_shows_section_title && !pageModule.recommendations_section_title) {
      return false
    }
    if (Object.entries(pageModule.category_pills).map((value) => { return value.some(i => (i.text && i.url && i.icon)) }).some(i => i === false)) {
      return false
    }
    if (Object.entries(pageModule.media_row).map((value) => { return value.some(i => (i.title && i.url)) }).some(i => i === false)) {
      return false
    }
    if (Object.entries(pageModule.marketing_pills).map((value) => { return value.some(i => (i.text && i.url && i.icon)) }).some(i => i === false)) {
      return false
    }
    if (Object.entries(pageModule.content_cards.cards).map((value) => { return value.some(i => (i.title && i.cta.url && i.cta.name)) }).some(i => i === false)) {
      return false
    }
    return true
  }

  const handleSlugChange = event => {
    const slug = event.target.value
    setPageModule({
      ...pageModule,
      slug
    })
  }

  const handleModalClose = () => {
    resetPageActionStatus()
  }

  const handleHeroTitleChange = event => {
    const title = event.target.value
    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        title
      }
    })
  }

  const handleMediaRow = mediaRows => {
    setPageModule({
      ...pageModule,
      media_row: mediaRows
    })
  }

  const handleMarketingPills = marketingPills => {
    setPageModule({
      ...pageModule,
      marketing_pills: marketingPills
    })
  }

  const handleCategoryPills = categoryPills => {
    setPageModule({
      ...pageModule,
      category_pills: categoryPills
    })
  }

  const handleContentCardSectionHeaderChange = (event) => {
    const { content_cards: contentCards = { cards: [] } } = pageModule
    setPageModule({
      ...pageModule,
      content_cards: {
        ...contentCards,
        header: event.target.value
      }
    })
  }

  const handleFlockler = updatedFlockler => {
    const { social_feeds: flockler } = pageModule
    setPageModule({
      ...pageModule,
      social_feeds: {
        ...flockler,
        ...updatedFlockler
      }
    })
  }

  const handleHorizontalBanner = updatedBanner => {
    const { banner: clonedbanner } = pageModule
    setPageModule({
      ...pageModule,
      banner: {
        ...clonedbanner,
        ...updatedBanner
      }
    })
  }

  const addNewMediaIcon = (event) => {
    event.stopPropagation()
    const { media_row: clonedMediaRow } = pageModule
    const mediaIcon = {
      title: '',
      url: '',
      icon: {
        url: '',
        alt: ''
      }
    }
    setPageModule({
      ...pageModule,
      media_row: [...clonedMediaRow, mediaIcon]
    })
  }

  const addNewCategoryPill = (event) => {
    event.stopPropagation()
    const { category_pills: clonedPills } = pageModule
    const pillObj = {
      text: '',
      url: '',
      icon: ''
    }
    setPageModule({
      ...pageModule,
      category_pills: [...clonedPills, pillObj]
    })
  }

  const addNewContentCardPill = (event) => {
    event.stopPropagation()
    const { content_cards: contentCards } = pageModule
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      image: {}
    }
    setPageModule({
      ...pageModule,
      content_cards: {
        ...contentCards,
        cards: [
          ...contentCards.cards,
          cardObj
        ]
      }
    })
  }

  const addNewMarketingPill = (event) => {
    event.stopPropagation()
    const { marketing_pills: clonedPills } = pageModule
    const pillObj = {
      text: '',
      url: '',
      icon: ''
    }
    setPageModule({
      ...pageModule,
      marketing_pills: [...clonedPills, pillObj]
    })
  }

  const deleteAllCategoryPills = (event) => {
    event.stopPropagation()
    setPageModule({
      ...pageModule,
      category_pills: []
    })
  }

  const deleteAllMarketingPills = (event) => {
    event.stopPropagation()
    setPageModule({
      ...pageModule,
      marketing_pills: []
    })
  }

  const deleteAllMediaIcon = (event) => {
    event.stopPropagation()
    setPageModule({
      ...pageModule,
      media_row: []
    })
  }

  const handleHeroButtonLabelChange = event => {
    const buttonName = event.target.value
    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        button_name: buttonName
      }
    })
  }
  const handleHeroPhoneNumberChange = event => {
    const phoneNumber = event.target.value
    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        phone_number: phoneNumber
      }
    })
  }

  const handleHeroDescriptionChange = description => {
    const { hero } = pageModule

    if (description.length < 156) {
      setPageModule((pageModule) => ({
        ...pageModule,
        hero: {
          ...hero,
          description
        }
      }))
    }
  }
  const handleHeroSubTitleChange = event => {
    const subTitle = event.target.value
    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        sub_title: subTitle
      }
    })
  }
  const handleHeroImageUrlUpdate = (thumbnailImageUrl, secureImageURL) => {
    const imageUrl = secureImageURL || thumbnailImageUrl
    const { hero } = pageModule

    const alt = getFileName(imageUrl)
    const { image } = hero
    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        image: {
          ...image,
          alt,
          src: imageUrl
        }
      }
    })
  }
  const handleImageChange = (imgObj) => {
    const { hero } = pageModule
    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        image: imgObj
      }
    })
  }
  const handleImageUrlChange = (secureImageURL) => {
    const { hero } = pageModule
    const alt = getFileName(secureImageURL)
    const { image } = hero
    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        image: {
          ...image,
          alt,
          src: secureImageURL
        }
      }
    })
  }
  const handleHeroChatLabelChange = event => {
    const chatButton = event.target.value
    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        chat_button: chatButton
      }
    })
  }

  const handleHeroEmailChange = event => {
    const email = event.target.value
    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        email
      }
    })
  }

  const handlePageMetaDescriptionChange = (description) => {
    const { page_meta: pageMeta } = pageModule

    setPageModule({
      ...pageModule,
      page_meta: {
        ...pageMeta,
        description
      }
    })
  }

  const handlePageMetaHeadingChange = (event) => {
    const { page_meta: pageMeta } = pageModule
    const heading = event.target.value

    setPageModule({
      ...pageModule,
      page_meta: {
        ...pageMeta,
        heading
      }
    })
  }

  const handlePageMetaKeywordsChange = (event) => {
    const keywords = event.target.value
    const { page_meta: pageMeta } = pageModule

    setPageModule({
      ...pageModule,
      page_meta: {
        ...pageMeta,
        keywords
      }
    })
  }

  const handlePageMetaShowSEOChange = (event) => {
    const showSEO = event.target.checked
    const { page_meta: pageMeta } = pageModule

    setPageModule({
      ...pageModule,
      page_meta: {
        ...pageMeta,
        should_show_seo: showSEO
      }
    })
  }

  const handlePageMetaTextChange = (text) => {
    const { page_meta: pageMeta } = pageModule

    setPageModule({
      ...pageModule,
      page_meta: {
        ...pageMeta,
        text
      }
    })
  }

  const handleCalendarTitleChange = event => {
    const title = event.target.value
    const { page_meta: pageMeta } = pageModule
    setPageModule({
      ...pageModule,
      calendar: {
        ...pageMeta,
        title
      }
    })
  }

  const handleShowCalendar = (event) => {
    event.stopPropagation()

    const { calendar } = pageModule

    setPageModule({
      ...pageModule,
      calendar: {
        ...calendar,
        show_calendar: event.target.checked
      }
    })
  }

  const handleCalendarDescriptionChange = (description) => {
    const { calendar } = pageModule

    setPageModule({
      ...pageModule,
      calendar: {
        ...calendar,
        description
      }
    })
  }

  const handlePageMetaTitleChange = (event) => {
    const title = event.target.value
    const { page_meta: pageMeta } = pageModule

    setPageModule({
      ...pageModule,
      page_meta: {
        ...pageMeta,
        title
      }
    })
  }

  const handleRecommendationSectionTitle = (event) => {
    setPageModule({
      ...pageModule,
      recommendations_section_title: event.target.value
    })
  }

  const handleUpcomingShowsSectionTitle = (event) => {
    setPageModule({
      ...pageModule,
      upcoming_shows_section_title: event.target.value
    })
  }

  const handlePageTypeChange = selectedType => {
    console.log('selectedType', selectedType)
    setPageModule({
      ...pageModule,
      type: selectedType
    })
  }

  const handlePageSubtypeChange = value => {
    const subtype = value || ''
    const next = { ...pageModule, subtype }
    if (pageModule?.type === 'universal-template' && pageModule?.slug) {
      const suf = getUniversalTemplateSlugSuffix(pageModule.slug, pageModule.subtype)
      if (suf && UNIVERSAL_TEMPLATE_SLUG_SUFFIX_RE.test(suf)) {
        const seg = normalizeUniversalTemplateSubtype(subtype || undefined)
        next.slug = `${getUniversalTemplateSlugPrefix(seg)}${suf}`
      }
    }
    setPageModule(next)
  }

  const handleAddUniversalModule = moduleType => {
    setPageModule({
      ...pageModule,
      modules: [...(pageModule.modules || []), createEmptyUniversalModule(moduleType)]
    })
  }

  const handleDeleteUniversalModule = (event, index) => {
    event.stopPropagation()
    const modules = [...(pageModule.modules || [])]
    modules.splice(index, 1)
    setPageModule({
      ...pageModule,
      modules
    })
  }

  const handleUniversalModuleUpdate = (index, updated) => {
    const modules = [...(pageModule.modules || [])]
    modules[index] = updated
    setPageModule({
      ...pageModule,
      modules
    })
  }

  const handleExpiresAtChange = (momentValue) => {
    setPageModule({
      ...pageModule,
      expires_at: momentValue ? momentValue.toISOString() : null
    })
  }

  const handleUniversalModuleReorder = (index, direction) => {
    const modules = [...(pageModule.modules || [])]
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= modules.length) return
    ;[modules[index], modules[swapIndex]] = [modules[swapIndex], modules[index]]
    setPageModule({ ...pageModule, modules })
  }

  const handleFormModuleIdUpdate = (event) => {
    const formstackId = event.target.value

    const { form } = pageModule

    setPageModule({
      ...pageModule,
      form: {
        ...form,
        formstack_id: formstackId
      }
    })
  }

  const handleFormModuleDescriptionChange = (description) => {
    const { form } = pageModule

    if (description.length < 156) {
      setPageModule((pageModule) => ({
        ...pageModule,
        form: {
          ...form,
          description
        }
      }))
    }
  }

  const handleGallerySectionTitle = (event) => {
    setPageModule({
      ...pageModule,
      galleryHeader: event.target.value
    })
  }

  const handleGalleryChange = (gallery) => {
    setGallery(gallery)
    setPageModule({
      ...pageModule,
      gallery
    })
  }

  const handleFormModuleHeaderChange = (header) => {
    const { form } = pageModule

    if (header.length < 71) {
      setPageModule((pageModule) => ({
        ...pageModule,
        form: {
          ...form,
          header
        }
      }))
    }
  }

  const handleFormModuleDisclaimerChange = (disclaimer) => {
    const { form } = pageModule

    setPageModule({
      ...pageModule,
      form: {
        ...form,
        disclaimer
      }
    })
  }

  const handleButtonTextUpdate = (event) => {
    const submitButtonText = event.target.value
    const { form } = pageModule
    setPageModule({
      ...pageModule,
      form: {
        ...form,
        submit_button_text: submitButtonText
      }
    })
  }

  const handleFormModuleTitleChange = (title) => {
    const { form } = pageModule
    const { success_screen: successScreen } = form
    setPageModule({
      ...pageModule,
      form: {
        ...form,
        success_screen: {
          ...successScreen,
          title: title
        }
      }
    })
  }

  const handleFormModuleMessageChange = (message) => {
    const { form } = pageModule
    const { success_screen: successScreen } = form
    setPageModule({
      ...pageModule,
      form: {
        ...form,
        success_screen: {
          ...successScreen,
          message
        }
      }
    })
  }

  const handleFormModuleSuccessSubmitButtonTextUpdate = (event) => {
    const successScreenButtonText = event.target.value
    const { form } = pageModule
    const { success_screen: successScreen } = form
    setPageModule({
      ...pageModule,
      form: {
        ...form,
        success_screen: {
          ...successScreen,
          button_text: successScreenButtonText
        }
      }
    })
  }

  const addContentModule = (event) => {
    event.stopPropagation()
    const { content_modules: contentModules = [] } = pageModule
    setPageModule({
      ...pageModule,
      content_modules: [
        ...contentModules,
        { ...CONTENT_MODULE }
      ]
    })
  }

  const handleAddCta = (event, index) => {
    event.stopPropagation()
    const { cards = [] } = pageModule
    const updatedCards = [...cards]
    updatedCards[index].ctas = [
      ...updatedCards[index].ctas,
      {
        title: '',
        href: ''
      }
    ]
    setPageModule({
      ...pageModule,
      cards: updatedCards
    })
  }

  const handleDeleteCta = (event, ctaIndex, suiteIndex) => {
    event.stopPropagation()
    const { cards = [] } = pageModule
    const updatedCards = [...cards]
    const clonedCTAs = updatedCards[suiteIndex].ctas

    if (ctaIndex !== -1) {
      clonedCTAs.splice(ctaIndex, 1)
    }
    setPageModule({
      ...pageModule,
      cards: updatedCards
    })
  }

  const addSuitesCardModule = (event) => {
    event.stopPropagation()
    const { cards: suiteCards = [] } = pageModule
    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCards,
        {
          suitesAllEvents: availableEventsForPromotion,
          related_events: [],
          promotedEvents: [],
          ...SUITE_CARD_MODULE
        }
      ]
    })
  }

  const handleContentModuleHeaderChange = (event, index) => {
    const header = event.target.value
    const { content_modules: contentModules = [] } = pageModule
    contentModules[index].header = header
    setPageModule({
      ...pageModule,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const handleContentModuleContentChange = (paragraph, index) => {
    const { content_modules: contentModules = [] } = pageModule
    if (paragraph.length < 156) {
      contentModules[index].description = paragraph
      setPageModule((pageModule) => ({
        ...pageModule,
        content_modules: [
          ...contentModules
        ]
      }))
    }
  }

  const handleContentModuleImageUrlUpdate = (secureImageURL, index) => {
    const { content_modules: contentModules = [] } = pageModule
    contentModules[index].icon = secureImageURL
    setPageModule({
      ...pageModule,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const handleAutoplay = (event) => {
    event.stopPropagation()

    const { hero } = pageModule

    setPageModule({
      ...pageModule,
      hero: {
        ...hero,
        autoplay: event.target.checked
      }
    })
  }

  const handleContentModuleCTAUrlChange = (event, index) => {
    const { content_modules: contentModules = [] } = pageModule
    contentModules[index] = {
      ...contentModules[index],
      cta: {
        ...contentModules[index].cta,
        url: event.target.value
      }
    }
    setPageModule({
      ...pageModule,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const handleContentModuleCTANameChange = (event, index) => {
    const { content_modules: contentModules = [] } = pageModule
    contentModules[index] = {
      ...contentModules[index],
      cta: {
        ...contentModules[index].cta,
        name: event.target.value
      }
    }
    setPageModule({
      ...pageModule,
      content_modules: [
        ...contentModules
      ]
    })
  }

  const deleteContentModule = (event, index) => {
    event.stopPropagation()
    const { content_modules: contentModules = [] } = pageModule
    const updatedContentModules = contentModules.filter((_, i) => i !== index)
    setPageModule({
      ...pageModule,
      content_modules: [
        ...updatedContentModules
      ]
    })
  }

  const deleteSuitesCardModule = (event, index) => {
    event.stopPropagation()
    const { cards: suiteCards = [] } = pageModule
    const updatedCards = suiteCards.filter((_, i) => i !== index)
    setPageModule({
      ...pageModule,
      cards: [
        ...updatedCards
      ]
    })
  }

  const handleSuiteAmenitiesChange = (amenities, index) => {
    const { cards: suiteCard = [] } = pageModule
    const { filters } = suiteCard[index]
    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            amenities
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleAllEventsChange = (events, suiteIndex) => {
    const { cards: suiteCards = [] } = pageModule

    suiteCards[suiteIndex].suitesAllEvents = [...events]

    setPageModule({
      ...pageModule,
      cards: suiteCards
    })
  }

  const handleSelectedEventsChange = (selectedEvents, suiteIndex) => {
    const { cards: suiteCard = [] } = pageModule

    setPageModule((pageModule) => ({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, suiteIndex),
        {
          ...suiteCard[suiteIndex],
          promotedEvents: selectedEvents,
          related_events: selectedEvents.map(({ id }) => id)
        },
        ...suiteCard.slice(suiteIndex + 1)
      ]
    }))
  }

  const handleSuiteCapacityChange = (capacity, index) => {
    const { cards: suiteCard = [] } = pageModule
    const { filters } = suiteCard[index]

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            capacity
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteCardTypeChange = (type, index) => {
    const { cards: suiteCard = [] } = pageModule

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          type,
          suitesAllEvents: availableEventsForPromotion,
          related_events: [],
          promotedEvents: []
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteCardShortNameChange = (shortDesc, index) => {
    const { cards: suiteCard = [] } = pageModule
    const [filteredAllEvents] = allSuiteLevels.filter(suite => suite.type === shortDesc)
    const allEventItems = shortDesc ? filteredAllEvents?.allEvents : availableEventsForPromotion
    const allEventsFiltered = shortDesc ? filteredAllEvents?.events : []
    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          short_desc: shortDesc || '',
          suitesAllEvents: allEventItems,
          related_events: allEventsFiltered.map(({ id }) => id),
          promotedEvents: allEventsFiltered
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteEventIdeasChange = (eventideas, index) => {
    const { cards: suiteCard = [] } = pageModule
    const { filters } = suiteCard[index]

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            eventideas
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteEventTypeChange = (eventtype, index) => {
    const { cards: suiteCard = [] } = pageModule
    const { filters } = suiteCard[index]

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            eventtype
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteHospitalityChange = (hospitalitytype, index) => {
    const { cards: suiteCard = [] } = pageModule
    const { filters } = suiteCard[index]

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            hospitalitytype
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteSizeChange = (size, index) => {
    const { cards: suiteCard = [] } = pageModule
    const { filters } = suiteCard[index]

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          filters: {
            ...filters,
            size
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteLogoUpload = (secureImageURL, index) => {
    const { cards: suiteCard = [] } = pageModule
    const alt = getFileName(secureImageURL)

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          image: {
            alt,
            src: secureImageURL
          }
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteSubTitleChange = (event, index) => {
    const { cards: suiteCard = [] } = pageModule

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          subtitle: event.target.value
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleCTAHrefChange = (href, suiteIndex, ctaIndex) => {
    const { cards = [] } = pageModule
    const updatedCards = [...cards]
    const clonedCTAs = updatedCards[suiteIndex].ctas

    if (ctaIndex !== -1 && clonedCTAs[ctaIndex]) {
      clonedCTAs[ctaIndex].href = href
    }
    setPageModule({
      ...pageModule,
      cards: updatedCards
    })
  }

  const handleCTATitleChange = (title, suiteIndex, ctaIndex) => {
    const { cards = [] } = pageModule
    const updatedCards = [...cards]
    const clonedCTAs = updatedCards[suiteIndex].ctas

    if (ctaIndex !== -1 && clonedCTAs[ctaIndex]) {
      clonedCTAs[ctaIndex].title = title
    }
    setPageModule({
      ...pageModule,
      cards: updatedCards
    })
  }

  const handleVenueChange = (selectedVenue) => {
    if (pageModule?.type === 'universal-template') {
      const next = { ...pageModule, venue_id: selectedVenue || undefined }
      if (selectedVenue && venues?.length && pageModule?.name) {
        const slug = buildUniversalTemplateSlugFromName(pageModule.name, selectedVenue, venues, pageModule?.subtype)
        if (slug !== undefined) {
          next.slug = slug
        }
      }
      setPageModule(next)
    } else if (pageModule?.type !== 'tours') {
      const [filteredVenue] = venues.filter(venue => venue._id === selectedVenue)
      setPageModule({
        ...pageModule,
        ...(filteredVenue && { slug: filteredVenue?.slug, name: `${filteredVenue?.name} - VLP` }),
        venue_id: selectedVenue
      })
    } else {
      const [filteredVenue] = tourVenues.filter(venue => venue._id === selectedVenue)
      setPageModule({
        ...pageModule,
        ...(filteredVenue && { slug: filteredVenue?.slug, name: `${filteredVenue?.name}` }),
        venue_id: selectedVenue
      })
    }

    setVLPHeroPromotedEvents([])
    if (selectedVenue && pageModule?.type !== 'tours') {
      setShowSpinner(true)
      fetchEventsVenueById(selectedVenue)
    }
  }

  const handleSuiteFormStatus = (event, index) => {
    const { cards: suiteCard = [] } = pageModule

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          show_form: event.target.checked
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const handleSuiteTitleChange = (event, index) => {
    const { cards: suiteCard = [] } = pageModule

    setPageModule({
      ...pageModule,
      cards: [
        ...suiteCard.slice(0, index),
        {
          ...suiteCard[index],
          title: event.target.value
        },
        ...suiteCard.slice(index + 1)
      ]
    })
  }

  const deleteGallery = (event, dataFieldGalleryIndex) => {
    event.stopPropagation()

    const clonedGalleries = [...gallery]

    if (dataFieldGalleryIndex !== -1) {
      clonedGalleries.splice(dataFieldGalleryIndex, 1)
    }
    setGallery(clonedGalleries)
    setPageModule({
      ...pageModule,
      gallery: clonedGalleries
    })
  }

  const deleteAllGallery = (event) => {
    event.stopPropagation()

    setGallery([])
    setPageModule({
      ...pageModule,
      gallery: []
    })
  }

  const addGallery = (event) => {
    event.stopPropagation()
    const { gallery = [] } = pageModule

    gallery.push({
      title: '',
      src: '',
      alt: '',
      mediaType: '',
      subtitle: ''
    })
    setGallery(gallery)
    setPageModule({
      ...pageModule,
      gallery
    })
  }

  const handleGalleryImageAltChange = (event, galleryIndex) => {
    const { gallery = [] } = pageModule
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        alt: event.target.value
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageModule({
      ...pageModule,
      gallery: updatedGallery
    })
  }

  const handleGalleryImageUrlUpdate = (imageUrl, galleryIndex) => {
    const { gallery = [] } = pageModule

    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        src: imageUrl
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageModule({
      ...pageModule,
      gallery: updatedGallery
    })
  }

  const handleGallerySubTitleChange = (event, galleryIndex) => {
    const { gallery = [] } = pageModule
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        subtitle: event.target.value
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageModule({
      ...pageModule,
      gallery: updatedGallery
    })
  }

  const handleGalleryTitleChange = (event, galleryIndex) => {
    const { gallery = [] } = pageModule
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        title: event.target.value
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageModule({
      ...pageModule,
      gallery: updatedGallery
    })
  }

  const handleGalleryMediaChange = (selectedItems, galleryIndex) => {
    const { gallery = [] } = pageModule
    const updatedGallery = [
      ...gallery.slice(0, galleryIndex),
      {
        ...gallery[galleryIndex],
        mediaType: selectedItems
      },
      ...gallery.slice(galleryIndex + 1)
    ]
    setGallery(updatedGallery)
    setPageModule({
      ...pageModule,
      gallery: updatedGallery
    })
  }

  const handleContentCard = cards => {
    setPageModule({
      ...pageModule,
      content_cards: {
        ...pageModule.content_cards,
        cards
      }
    })
  }

  const addNewLink = (event) => {
    event.stopPropagation()
    let { anchor_links: clonedLinks } = pageModule
    const linkObj = {
      label: '',
      key: ''
    }
    if (!clonedLinks) {
      clonedLinks = []
    }
    setPageModule({
      ...pageModule,
      anchor_links: [...clonedLinks, linkObj]
    })
  }

  const deleteAllLink = (event) => {
    event.stopPropagation()
    setPageModule({
      ...pageModule,
      anchor_links: []
    })
  }

  const setAnchorLinks = links => {
    setPageModule({
      ...pageModule,
      anchor_links: [
        ...links
      ]
    })
  }

  const handleChatbotStatus = status => {
    setPageModule({
      ...pageModule,
      show_chatbot: status
    })
  }

  const handleOptinFormStatus = status => {
    setPageModule({
      ...pageModule,
      show_optin_form: status
    })
  }

  const handleChatPopUpIdChange = event => {
    setPageModule({
      ...pageModule,
      chat_pop_up_id: event.target.value
    })
  }

  const toggleSwitch = <Switch checked={pageModule?.show_chatbot ?? false} checkedChildren="Show Satisfi" defaultChecked onChange={handleChatbotStatus} unCheckedChildren="Hide Satisfi" />
  const optinFormToggleSwitch = (
    <Switch
      checked={pageModule?.show_optin_form ?? false}
      checkedChildren="Show opt-in form"
      onChange={handleOptinFormStatus}
      unCheckedChildren="Hide opt-in form"
    />
  )

  const isUniversalTemplate = pageModule?.type === 'universal-template'
  const universalTemplateErrors = isUniversalTemplate ? {
    name: !pageModule?.name?.trim() ? 'Name is required' : '',
    venueId: !pageModule?.venue_id ? 'Venue is required' : '',
    slug: !isValidUniversalTemplateSlug(pageModule?.slug, pageModule?.subtype)
      ? `Slug must be ${getUniversalTemplateSlugPrefix(pageModule?.subtype)}<valid-slug>`
      : '',
    pageExpiresAt: pageModule?.expires_at ? '' : 'Page expiry time is required',
    expiresAt: (() => {
      const formMod = (pageModule?.modules || []).find(m => m.type === 'form')
      if (!formMod?.form) return ''
      const formExpires = formMod.form.expires_at
      if (!formExpires) return 'Form expiry time is required'
      const pageExpires = pageModule?.expires_at
      if (pageExpires) {
        const formMs = new Date(formExpires).getTime()
        const pageMs = new Date(pageExpires).getTime()
        if (!Number.isNaN(formMs) && !Number.isNaN(pageMs) && formMs >= pageMs) {
          return 'Form expiry time must be before page expiry time'
        }
      }
      return ''
    })(),
    formCustomFields: (() => {
      const formMod = (pageModule?.modules || []).find(m => m.type === 'form')
      const cf = formMod?.form?.custom_fields
      if (!cf || typeof cf !== 'object') return ''
      return Object.keys(cf).some(k => !String(k).trim())
        ? 'Each custom field must have a key'
        : ''
    })(),
    formUrl: (() => {
      const formMod = (pageModule?.modules || []).find(m => m.type === 'form')
      if (!formMod?.form) return ''
      return getFormUrlValidationMessage(formMod.form.url)
    })(),
    modules: (pageModule?.modules || []).length === 0 ? 'At least one module is required' : ''
  } : {}
  const addPageButton = (
    <Button className={styles.updateActionButton} loading={!isDataValid} onClick={handleSubmit} type='primary'>Add</Button>
  )
  const addPagesButton = (
    <Button
      onClick={() => {
        resetPageActionStatus()
        window.location.reload()
      }}
      type="primary"
    >
      Add Another
    </Button>
  )

  const handlePriceCardSectionHeaderChange = (event) => {
    const { tours_prices: tourPrices = {} } = pageModule
    setPageModule({
      ...pageModule,
      tours_prices: {
        ...tourPrices,
        header: event.target.value
      }
    })
  }
  const deleteAllPriceCard = (event) => {
    event.stopPropagation()
    setPageModule({
      ...pageModule,
      tours_prices: []
    })
  }
  const addPriceCard = (event) => {
    event.stopPropagation()
    const { tours_prices: priceCards } = pageModule
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      image: {}
    }
    setPageModule({
      ...pageModule,
      tours_prices: {
        ...priceCards,
        cards: [
          ...priceCards.cards,
          cardObj
        ]
      }
    })
  }

  const handlePriceCardDisclaimerChange = (disclaimer) => {
    const { tours_prices: tourPrices = {} } = pageModule
    setPageModule({
      ...pageModule,
      tours_prices: {
        ...tourPrices,
        disclaimer
      }
    })
  }

  const handleTourPlanDescriptionChange = (description) => {
    const { tours_prices: tourPrices = {} } = pageModule
    setPageModule({
      ...pageModule,
      tours_prices: {
        ...tourPrices,
        html_description: description
      }
    })
  }

  const handlePriceCard = cards => {
    setPageModule({
      ...pageModule,
      tours_prices: {
        ...pageModule.tours_prices,
        cards
      }
    })
  }

  const addNewPriceCard = (event) => {
    event.stopPropagation()
    const { tours_prices: tourPrices } = pageModule
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      image: {}
    }
    setPageModule({
      ...pageModule,
      tours_prices: {
        ...tourPrices,
        cards: [
          ...tourPrices.cards,
          cardObj
        ]
      }
    })
  }

  const goToListPagesButton = (
    <Route key="listPages" render={({ history }) => (
      <Button
        onClick={() => {
          resetPageActionStatus()
          history.push('/pages')
          window.location.reload()
        }}
        type="primary"
      >
        Go to Pages List
      </Button>
    )} />
  )

  const editPageContent = (
    <Route key="editPageContent" render={({ history }) => (

      <Button
        onClick={() => {
          newPageId && history.push(`/pages/${newPageId}/edit`)
          window.location.reload()
        }}
        type="primary"
      >
        Keep Editing
      </Button>
    )} />
  )

  const handleMarketingModules = marketingModules => {
    setPageModule({
      ...pageModule,
      marketing_modules: marketingModules
    })
  }

  const addNewMarketingModule = (event) => {
    event.stopPropagation()
    const { marketing_modules: clonedPills } = pageModule
    const marketingModules = {
      text: '',
      subtitle: '',
      icon: {},
      cta: { url: '', name: '' }
    }
    if (!clonedPills) {
      setPageModule({
        ...pageModule,
        marketing_modules: [marketingModules]
      })
      return
    }
    setPageModule({
      ...pageModule,
      marketing_modules: [...clonedPills, marketingModules]
    })
  }

  const deleteAllMarketingModules = (event) => {
    event.stopPropagation()
    setPageModule({
      ...pageModule,
      marketing_modules: []
    })
  }

  const handleFAQs = cards => {
    setPageModule({
      ...pageModule,
      faqs: {
        ...pageModule.faqs,
        flex_modules: cards
      }
    })
  }

  const handleFAQsSectionHeaderChange = (event) => {
    const { faqs: faqsCards } = pageModule
    setPageModule({
      ...pageModule,
      faqs: {
        ...faqsCards,
        header: event.target.value
      }
    })
  }

  const addNewFAQ = (event) => {
    event.stopPropagation()
    const { faqs: faqsCards } = pageModule
    const cardObj = {
      title: '',
      subtitle: '',
      cta: {},
      images: []
    }
    setPageModule({
      ...pageModule,
      faqs: {
        ...faqsCards,
        flex_modules: [
          ...(faqsCards?.flex_modules ? faqsCards?.flex_modules : []),
          cardObj
        ]
      }
    })
  }

  const addNewFAQImage = (card, rowIndex) => {
    const { faqs: faqsCards } = pageModule
    const cardImgObj = {
      url: '',
      alt: '',
      href: ''
    }
    faqsCards?.flex_modules?.[rowIndex]?.images?.push(cardImgObj)

    console.log(faqsCards)
    setPageModule({
      ...pageModule,
      faqs: {
        ...faqsCards
      }
    })
  }

  const handleOverviewDescriptionChange = (description) => {
    const { overview } = pageModule

    setPageModule({
      ...pageModule,
      overview: {
        ...overview,
        html_description: description
      }
    })
  }

  const handleOverviewSectionHeaderChange = (event) => {
    const { overview = {} } = pageModule
    setPageModule({
      ...pageModule,
      overview: {
        ...overview,
        header: event.target.value
      }
    })
  }

  const deleteAllFAQsImages = (rowIndex) => {
    const { faqs: faqsCards } = pageModule
    const { flex_modules: flexModules } = faqsCards

    setPageModule({
      ...pageModule,
      faqs: {
        ...faqsCards,
        flex_modules: [
          ...flexModules.slice(0, rowIndex),
          {
            ...flexModules[rowIndex],
            images: []
          },
          ...flexModules.slice(rowIndex + 1)
        ]
      }
    })
  }

  const deleteAllPriceCards = (event) => {
    event.stopPropagation()
    const { tours_prices: tourPrices = {} } = pageModule

    setPageModule({
      ...pageModule,
      tours_prices: {
        ...tourPrices,
        cards: []
      }
    })
  }

  const deleteAllFAQs = (event) => {
    event.stopPropagation()

    const { faqs = {} } = pageModule

    setPageModule({
      ...pageModule,
      faqs: {
        ...faqs,
        flex_modules: []
      }
    })
  }

  return (
    <Layout className={styles['new-page-container']}>
      <PageHeader
        className={styles.headline}
        extra={(
          <Space align="center" size="middle" wrap>
            {toggleSwitch}
            {optinFormToggleSwitch}
            {addPageButton}
          </Space>
        )}
        title={PAGE_TITLE}
      />
      <Layout.Content>
        <PageBasicInfo
          addContentModule={addContentModule}
          addGallery={addGallery}
          addNewCategoryPill={addNewCategoryPill}
          addNewContentCardPill={addNewContentCardPill}
          addNewFAQ={addNewFAQ}
          addNewFAQImage={addNewFAQImage}
          addNewLink={addNewLink}
          addNewMarketingModule={addNewMarketingModule}
          addNewMarketingPill={addNewMarketingPill}
          addNewMediaIcon={addNewMediaIcon}
          addNewPriceCard={addNewPriceCard}
          addPriceCard={addPriceCard}
          addSuitesCardModule={addSuitesCardModule}
          allSuiteLevels={allSuiteLevels}
          deleteAllCategoryPills={deleteAllCategoryPills}
          deleteAllFAQs={deleteAllFAQs}
          deleteAllFAQsImages={deleteAllFAQsImages}
          deleteAllGallery={deleteAllGallery}
          deleteAllLink={deleteAllLink}
          deleteAllMarketingModules={deleteAllMarketingModules}
          deleteAllMarketingPills={deleteAllMarketingPills}
          deleteAllMediaIcon={deleteAllMediaIcon}
          deleteAllPriceCard={deleteAllPriceCard}
          deleteAllPriceCards={deleteAllPriceCards}
          deleteContentModule={deleteContentModule}
          deleteGallery={deleteGallery}
          deleteSuitesCardModule={deleteSuitesCardModule}
          galleries={gallery}
          gallery_header={pageModule?.galleryHeader || ''}
          handleAddCta={handleAddCta}
          handleAddUniversalModule={handleAddUniversalModule}
          handleAllEventsChange={handleAllEventsChange}
          handleAutoplay={handleAutoplay}
          handleButtonTextUpdate={handleButtonTextUpdate}
          handleCTAHrefChange={handleCTAHrefChange}
          handleCTATitleChange={handleCTATitleChange}
          handleCalendarDescriptionChange={handleCalendarDescriptionChange}
          handleCalendarTitleChange={handleCalendarTitleChange}
          handleCategoryPills={handleCategoryPills}
          handleChatPopUpIdChange={handleChatPopUpIdChange}
          handleContentCard={handleContentCard}
          handleContentCardSectionHeaderChange={handleContentCardSectionHeaderChange}
          handleContentModuleCTANameChange={handleContentModuleCTANameChange}
          handleContentModuleCTAUrlChange={handleContentModuleCTAUrlChange}
          handleContentModuleContentChange={handleContentModuleContentChange}
          handleContentModuleHeaderChange={handleContentModuleHeaderChange}
          handleContentModuleImageUrlUpdate={handleContentModuleImageUrlUpdate}
          handleDeleteCta={handleDeleteCta}
          handleDeleteUniversalModule={handleDeleteUniversalModule}
          handleExpiresAtChange={handleExpiresAtChange}
          handleFAQs={handleFAQs}
          handleFAQsSectionHeaderChange={handleFAQsSectionHeaderChange}
          handleFlockler={handleFlockler}
          handleFormModuleDescriptionChange={handleFormModuleDescriptionChange}
          handleFormModuleDisclaimerChange={handleFormModuleDisclaimerChange}
          handleFormModuleHeaderChange={handleFormModuleHeaderChange}
          handleFormModuleIdUpdate={handleFormModuleIdUpdate}
          handleFormModuleMessageChange={handleFormModuleMessageChange}
          handleFormModuleSuccessSubmitButtonTextUpdate={handleFormModuleSuccessSubmitButtonTextUpdate}
          handleFormModuleTitleChange={handleFormModuleTitleChange}
          handleGalleryChange={handleGalleryChange}
          handleGalleryImageAltChange={handleGalleryImageAltChange}
          handleGalleryImageUrlUpdate={handleGalleryImageUrlUpdate}
          handleGalleryMediaChange={handleGalleryMediaChange}
          handleGallerySectionTitle={handleGallerySectionTitle}
          handleGallerySubTitleChange={handleGallerySubTitleChange}
          handleGalleryTitleChange={handleGalleryTitleChange}
          handleHeroButtonLabelChange={handleHeroButtonLabelChange}
          handleHeroChatLabelChange={handleHeroChatLabelChange}
          handleHeroDescriptionChange={handleHeroDescriptionChange}
          handleHeroEmailChange={handleHeroEmailChange}
          handleHeroImageUrlUpdate={handleHeroImageUrlUpdate}
          handleHeroPhoneNumberChange={handleHeroPhoneNumberChange}
          handleHeroSubTitleChange={handleHeroSubTitleChange}
          handleHeroTitleChange={handleHeroTitleChange}
          handleHorizontalBanner={handleHorizontalBanner}
          handleImageChange={handleImageChange}
          handleImageUrlChange={handleImageUrlChange}
          handleMarketingPills={handleMarketingPills}
          handleMediaRow={handleMediaRow}
          handleOverviewDescriptionChange={handleOverviewDescriptionChange}
          handleOverviewSectionHeaderChange={handleOverviewSectionHeaderChange}
          handlePageMetaDescriptionChange={handlePageMetaDescriptionChange}
          handlePageMetaHeadingChange={handlePageMetaHeadingChange}
          handlePageMetaKeywordsChange={handlePageMetaKeywordsChange}
          handlePageMetaShowSEOChange={handlePageMetaShowSEOChange}
          handlePageMetaTextChange={handlePageMetaTextChange}
          handlePageMetaTitleChange={handlePageMetaTitleChange}
          handlePageNameChange={handlePageNameChange}
          handlePageSubtypeChange={handlePageSubtypeChange}
          handlePageTypeChange={handlePageTypeChange}
          handlePriceCard={handlePriceCard}
          handlePriceCardDisclaimerChange={handlePriceCardDisclaimerChange}
          handlePriceCardSectionHeaderChange={handlePriceCardSectionHeaderChange}
          handleRecommendationSectionTitle={handleRecommendationSectionTitle}
          handleSelectedEventsChange={handleSelectedEventsChange}
          handleShowCalendar={handleShowCalendar}
          handleSlugChange={handleSlugChange}
          handleSuiteAmenitiesChange={handleSuiteAmenitiesChange}
          handleSuiteCapacityChange={handleSuiteCapacityChange}
          handleSuiteCardShortNameChange={handleSuiteCardShortNameChange}
          handleSuiteCardTypeChange={handleSuiteCardTypeChange}
          handleSuiteEventIdeasChange={handleSuiteEventIdeasChange}
          handleSuiteEventTypeChange={handleSuiteEventTypeChange}
          handleSuiteFormStatus={handleSuiteFormStatus}
          handleSuiteHospitalityChange={handleSuiteHospitalityChange}
          handleSuiteLogoUpload={handleSuiteLogoUpload}
          handleSuiteSizeChange={handleSuiteSizeChange}
          handleSuiteSubTitleChange={handleSuiteSubTitleChange}
          handleSuiteTitleChange={handleSuiteTitleChange}
          handleTourPlanDescriptionChange={handleTourPlanDescriptionChange}
          handleUniversalModuleReorder={handleUniversalModuleReorder}
          handleUniversalModuleUpdate={handleUniversalModuleUpdate}
          handleUpcomingShowsSectionTitle={handleUpcomingShowsSectionTitle}
          handleVenueChange={handleVenueChange}
          mode='new'
          page={pageModule}
          pageTypes={PAGE_TYPES}
          recommendedEvents={recommendedEvents}
          selectedType={selectedType}
          setAnchorLinks={setAnchorLinks}
          setGallery={setGallery}
          setMarketingModules={handleMarketingModules}
          setVLPHeroAllEvents={setVLPHeroAllEvents}
          setVLPHeroPromotedEvents={setVLPHeroPromotedEvents}
          suitesAmenities={suitesAmenities}
          suitesCapacity={suitesCapacity}
          suitesEventIdeas={suitesEventIdeas}
          suitesEventType={suitesEventType}
          suitesHospitality={suitesHospitality}
          suitesSize={suitesSize}
          todaysEvent={todaysEvent}
          tourVenues={tourVenues}
          universalTemplateErrors={universalTemplateErrors}
          upcomingEvents={upcomingEvents}
          venues={venues}
          vlpHeroAllEvents={vlpHeroAllEvents}
          vlpHeroPromotedEvents={vlpHeroPromotedEvents}
        />
      </Layout.Content>
      {
        updateStatus &&
        <ResultModal
          actions={[
            editPageContent,
            goToListPagesButton,
            addPagesButton
          ]}
          handleCancel={handleModalClose}
          status="success"
          title={updateMessage}
        />
      }
      {
        updateStatus === false && updateMessage &&
        <ResultModal
          handleCancel={handleModalClose}
          status="error"
          title={updateMessage}
        />
      }
      <Spinner
        isLoading={showSpinner}
        size="large"
      />
    </Layout>
  )
}

const mapStateToProps = ({ pages, suiteCards }) => ({
  newPageId: pages.newPageId,
  updateMessage: pages.message,
  updateStatus: pages.success,
  recommendedEvents: pages.recommendedEvents,
  todaysEvent: pages.todaysEvent,
  upcomingEvents: pages.upcomingEvents,
  vlpHeroAllEvents: pages.vlpHeroAllEvents,
  suitesAmenities: suiteCards.suitesAmenities,
  suitesCapacity: suiteCards.suitesCapacity,
  suitesEventIdeas: suiteCards.suitesEventIdeas,
  suitesEventType: suiteCards.suitesEventType,
  suitesHospitality: suiteCards.suitesHospitality,
  suitesSize: suiteCards.suitesSize,
  availableEventsForPromotion: suiteCards.allEvents,
  allSuiteLevels: suiteCards.allSuiteLevels,
  venues: pages.venues,
  tourVenues: pages.tourVenues
})

const mapDispatchToProps = {
  addNewPage,
  fetchEventsVenueById,
  initializePages,
  newSuiteCardModule,
  resetPageActionStatus
}

NewPageModule.propTypes = {
  addNewPage: PropTypes.func,
  allSuiteLevels: PropTypes.array,
  availableEventsForPromotion: PropTypes.array,
  fetchEventsVenueById: PropTypes.func,
  initializePages: PropTypes.func,
  newPageId: PropTypes.string,
  newSuiteCardModule: PropTypes.func,
  promotedESLEvents: PropTypes.func,
  promotedISLEvents: PropTypes.array,
  promotedLSLEvents: PropTypes.array,
  recommendedEvents: PropTypes.array,
  resetPageActionStatus: PropTypes.func,
  sLEvents: PropTypes.array,
  suitesAmenities: PropTypes.array.isRequired,
  suitesCapacity: PropTypes.array.isRequired,
  suitesEventIdeas: PropTypes.array.isRequired,
  suitesEventType: PropTypes.array.isRequired,
  suitesHospitality: PropTypes.array.isRequired,
  suitesSize: PropTypes.array.isRequired,
  todaysEvent: PropTypes.array,
  tourVenues: PropTypes.array,
  upcomingEvents: PropTypes.array,
  updateMessage: PropTypes.string,
  updateStatus: PropTypes.bool,
  venues: PropTypes.array,
  vlpHeroAllEvents: PropTypes.array
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(NewPageModule)
